//#region imports
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import moment from 'moment';

// redux
import { useSelector } from 'react-redux';
import { selectUserDb } from '_redux/slices/main-screen/user';

// services
import { searchFlights } from '_services/flight-search-service';

// styling
import { Colors, Spacing } from '_styles';

// icons
import { MdFlight } from 'react-icons/md';

// custom components
import { CustomSpinner } from '_atoms';
import FlightSearchRow from './flight-search-row';

// virtualized list
import { Virtuoso } from 'react-virtuoso';
import { IoCloseCircle } from 'react-icons/io5';

//#endregion

const CARDS_PER_ROW = 4;

const FlightSearch = (props) => {

    // redux
    const userDb = useSelector(selectUserDb);
    const iata = userDb?.details?.authority || '';

    // search states
    const [searchTerm, setSearchTerm] = useState('');

    // API results state
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    // Ref to track if component is mounted
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (term) => {
            // Reset if search term is too short
            if (!term || term.length < 2) {
                if (isMounted.current) {
                    setSearchResults([]);
                    setHasSearched(false);
                    setError(null);
                }
                return;
            }

            if (isMounted.current) {
                setIsLoading(true);
                setError(null);
                setHasSearched(true);
            }

            try {
                const results = await searchFlights({
                    iata: iata,
                    date: moment().format('YYYY-MM-DD'),
                    searchTerm: term
                });

                if (isMounted.current) {
                    // Sort results by operation time
                    const sortedResults = (results || []).sort((a, b) => {
                        const timeA = new Date(a.operationInfo?.time || a.operationTime || a.flightTime);
                        const timeB = new Date(b.operationInfo?.time || b.operationTime || b.flightTime);
                        return timeA - timeB;
                    });
                    setSearchResults(sortedResults);
                }
            } catch (err) {
                console.error('Flight search error:', err);
                if (isMounted.current) {
                    setError(err.message || 'Error searching flights');
                    setSearchResults([]);
                }
            } finally {
                if (isMounted.current) {
                    setIsLoading(false);
                }
            }
        }, 400),
        [iata]
    );

    // Trigger search when searchTerm changes
    useEffect(() => {
        debouncedSearch(searchTerm);

        // Cleanup: cancel pending debounced calls
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchTerm, debouncedSearch]);

    // flight click handler
    const handleFlightClick = useCallback((flight) => {
        if (props.onFlightSelect) {
            props.onFlightSelect(flight);
        }
    }, [props.onFlightSelect]);

    // Group results into rows of CARDS_PER_ROW
    const groupedResults = useMemo(() => {
        const rows = [];
        for (let i = 0; i < searchResults.length; i += CARDS_PER_ROW) {
            rows.push(searchResults.slice(i, i + CARDS_PER_ROW));
        }
        return rows;
    }, [searchResults]);

    // Memoize row content renderer for Virtuoso
    const rowContent = useCallback((index) => {
        const rowFlights = groupedResults[index];
        return (
            <div style={{
                display: 'flex',
                gap: Spacing.SCALE_12,
                paddingBottom: Spacing.SCALE_12,
                borderBottom: '1px solid #e0e0e0',
                marginBottom: Spacing.SCALE_12,
            }}>
                {rowFlights.map((flight, idx) => (
                    <div key={flight._id || flight.bookingKey || `${flight.flightNumber}-${idx}`} style={{ flex: 1, minWidth: 0 }}>
                        <FlightSearchRow
                            flight={flight}
                            onClick={() => handleFlightClick(flight)}
                        />
                    </div>
                ))}
                {/* Fill empty slots to maintain grid alignment */}
                {rowFlights.length < CARDS_PER_ROW &&
                    Array(CARDS_PER_ROW - rowFlights.length).fill(null).map((_, idx) => (
                        <div key={`empty-${idx}`} style={{ flex: 1, minWidth: 0 }} />
                    ))
                }
            </div>
        );
    }, [groupedResults, handleFlightClick]);

    return (
        <div style={{
            border: '1px solid lightgrey',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            borderTopWidth: '4px',
            borderTopColor: Colors.PRM_LIGHT_BLUE
        }}>

            {/* Search Controls */}
            <div style={{
                padding: Spacing.SCALE_12,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f9f9f9',
            }}>
                {/* Search Input with Icon */}
                <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                    <MdFlight size={24} color={Colors.PRM_LIGHT_BLUE} />
                    <div style={{ flex: 1, position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Enter flight number to search (include carrier code)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value?.toUpperCase())}
                            style={{
                                width: '100%',
                                padding: Spacing.SCALE_8,
                                paddingRight: searchTerm ? '32px' : Spacing.SCALE_8,
                                border: '1px solid #ccc',
                                borderRadius: 4,
                                fontSize: 14,
                                boxSizing: 'border-box',
                            }}
                        />
                        {searchTerm && (
                            <IoCloseCircle
                                size={20}
                                color="#999"
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Results Count / Status */}
                <div style={{
                    marginTop: Spacing.SCALE_8,
                    fontSize: 12,
                    color: '#666'
                }}>
                    {isLoading ? (
                        'Searching...'
                    ) : hasSearched ? (
                        `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} found`
                    ) : (
                        'Enter a search term to find flights (include carrier code)'
                    )}
                </div>
            </div>

            {/* Results List */}
            <div style={{
                flex: 1,
                padding: Spacing.SCALE_8,
                position: 'relative',
            }}>
                {/* Loading State */}
                {isLoading && (
                    <CustomSpinner rowCenter={true} />
                )}

                {/* Error State */}
                {error && !isLoading && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: 'red'
                    }}>
                        {error}
                    </div>
                )}

                {/* Empty State - No search yet */}
                {!isLoading && !error && !hasSearched && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: '#666',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <MdFlight size={64} color={Colors.PRM_LIGHT_BLUE} style={{ marginBottom: Spacing.SCALE_16 }} />
                        <p style={{ marginBottom: Spacing.SCALE_8 }}>Flight Search</p>
                        <p style={{ fontSize: 12 }}>{"Enter a flight number (with the carrier code) to search for a flight"}</p>
                    </div>
                )}

                {/* Empty State - No results */}
                {!isLoading && !error && hasSearched && searchResults.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: Spacing.SCALE_24,
                        color: '#666'
                    }}>
                        No flights found matching "{searchTerm}"
                    </div>
                )}

                {/* Results List - 3 cards per row */}
                {!isLoading && !error && searchResults.length > 0 && (
                    <Virtuoso
                        style={{ height: '100%', width: '100%' }}
                        totalCount={groupedResults.length}
                        overscan={3}
                        itemContent={rowContent}
                    />
                )}
            </div>
        </div>
    );
};

export default FlightSearch;
